// Title management service for handling conversation title editing functionality
import React from 'react';
import { Conversation } from './chatTypes';
import config from '../../config';

const BASE_URL = config.apiUrl;

export interface TitleManagementConfig {
  editingTitle: string;
  editingTitleId: number | null;
}

export interface TitleManagementCallbacks {
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
  setEditingTitleId: (id: number | null) => void;
  setEditingTitle: (title: string) => void;
}

// Update conversation title on the server and in local state
export async function updateConversationTitle(
  conversationId: number,
  newTitle: string,
  callbacks: TitleManagementCallbacks
): Promise<void> {
  const token = localStorage.getItem('accessToken');
  const tokenType = localStorage.getItem('tokenType');
  if (!token || !newTitle.trim()) return;

  try {
    const response = await fetch(`${BASE_URL}/conversations/${conversationId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `${tokenType} ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ title: newTitle.trim() })
    });

    if (response.ok) {
      // Update conversations list
      callbacks.setConversations(prev => prev.map(conv => 
        conv.id === conversationId ? { ...conv, title: newTitle.trim() } : conv
      ));
      // Exit edit mode
      callbacks.setEditingTitleId(null);
    } else {
      console.error('Failed to update conversation title');
    }
  } catch (error) {
    console.error('Error updating conversation title:', error);
  }
}

// Handle keyboard events for title editing (Enter to submit, Escape to cancel)
export function handleTitleSubmit(
  conversationId: number,
  event: React.KeyboardEvent,
  config: TitleManagementConfig,
  callbacks: TitleManagementCallbacks
): void {
  if (event.key === 'Enter') {
    event.preventDefault();
    updateConversationTitle(conversationId, config.editingTitle, callbacks);
  } else if (event.key === 'Escape') {
    callbacks.setEditingTitleId(null);
  }
}

// Initialize title editing mode
export function startEditing(
  e: React.MouseEvent,
  conversation: Conversation,
  callbacks: TitleManagementCallbacks
): void {
  e.stopPropagation(); // Prevent triggering conversation selection
  callbacks.setEditingTitleId(conversation.id);
  callbacks.setEditingTitle(conversation.title || 'New Chat');
}

// Process title update by validating input and calling update function
export async function handleTitleUpdate(
  conversationId: number,
  config: TitleManagementConfig,
  callbacks: TitleManagementCallbacks
): Promise<void> {
  if (!config.editingTitle.trim()) {
    callbacks.setEditingTitleId(null);
    return;
  }

  await updateConversationTitle(conversationId, config.editingTitle, callbacks);
  callbacks.setEditingTitleId(null);
}